/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { showSuccess } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { ServiceCommitment } from "~/models";
import { ROUTES } from "~/routing";
import {
  checkValue,
  checkVersion,
  generateActionSuccessText,
  useAxios,
} from "~/tools";

type Values = {
  version: string;
};

type AddNewVersionProps = {
  service: ServiceCommitment;
  refetchSLA: (id?: string) => Promise<void>;
};

function AddNewVersion({ service, refetchSLA }: AddNewVersionProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const methods = useForm();

  const history = useHistory();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    const organizeValues = {
      ...service,
      version: checkValue(values.version),
    };
    try {
      const { data } = await Axios().post("/catalog/services", organizeValues);
      showSuccess(
        generateActionSuccessText(`New Version of ${service.name}`)()("added")()
      );
      refetchSLA(data.id);

      history.push(`${ROUTES.serviceCommitments.index}/${data.id}`);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsSidebarOpen(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        css={{ marginRight: "1rem" }}
        icon="plus"
        severity="medium"
      >
        Add new Version
      </Button>

      <SidebarForm
        title="Add new version"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField
          name="version"
          label="Version"
          placeholder="2021-01"
          registerOptions={{ validate: checkVersion }}
        />
        <SidebarFormButtons
          submitEnabled={!!methods.watch("version")}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { AddNewVersion };
